import {css, StyleSheet} from 'aphrodite';
import React, {useRef} from 'react';

import AntibodyChainLegend from 'antibodies/antibody-map/AntibodyChainLegend';
import AntibodyDragWrapper from 'antibodies/antibody-map/AntibodyDragWrapper';
import {
  clampAntibodyPreviewCoordinatesToBoundingBox,
  getDefaultAntibodyPreviewCoordinates,
} from 'antibodies/antibody-map/AntibodyMapUtils';
import ComponentPreview from 'antibodies/antibody-map/ComponentPreview';
import useAntibodyMapState, {
  DEFAULT_ANTIBODY_DRAG_COORDINATE,
} from 'antibodies/antibody-map/useAntibodyMapState';
import AntibodyFeedbackBanner from 'antibodies/AntibodyFeedbackBanner';
import {Antibody as GQLAntibody, AntibodyWithVersion} from 'antibodies/AntibodyTypedefs';
import useAntibodyUIState from 'antibodies/hooks/useAntibodyUIState';
import {useAntibodyVersionQuery} from 'antibodies/hooks/useAntibodyVersionQuery';
import Antibody from 'antibodies/visualization/Antibody';
import {assert} from 'Asserts';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import getTestProps from 'test/lib/cypress/getTestProps';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

function assertIsNonNullishAntibodyAndVersion(
  antibody: GQLAntibody | null
): asserts antibody is AntibodyWithVersion {
  assert(antibody != null && antibody.version != null, 'Unexpected nullish antibody or version');
}

export interface AntibodyMapProps {
  antibodyId: string;
}

export default function AntibodyMap({antibodyId}: AntibodyMapProps): JSX.Element {
  const {
    state: {openVersionId},
  } = useAntibodyUIState(antibodyId);
  const {status, antibody} = useAntibodyVersionQuery(antibodyId, openVersionId);

  const {
    state: {
      dragState: {x, y},
      expandPreview,
      hoveredRef,
      selectedRef,
      showLabels,
      showMinimap,
    },
    setExpandPreview,
    setHoveredRef,
    setSelectedRef,
  } = useAntibodyMapState(antibodyId);

  const domainPreviewRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const toggleExpandPreview = (): void => {
    let newX = x;
    let newY = y;
    if (wrapperRef.current != null) {
      // If we are expanding the preview for the first time (i.e., if we are not currently expanded and the
      // location of the antibody viz is at default)...
      if (
        !expandPreview &&
        x === DEFAULT_ANTIBODY_DRAG_COORDINATE &&
        y === DEFAULT_ANTIBODY_DRAG_COORDINATE
      ) {
        // ...put the antibody preview in the default location.
        [newX, newY] = getDefaultAntibodyPreviewCoordinates(wrapperRef.current);
      }
      // Clamp the coordinates just in case we've changed viewbox size since last viewing the expanded preview.
      [newX, newY] = clampAntibodyPreviewCoordinatesToBoundingBox(wrapperRef.current, newX, newY);
    }
    setExpandPreview(!expandPreview, newX, newY);
  };

  if (status === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (status === QueryResultType.ERROR || status === QueryResultType.SKIPPED) {
    return <span>Error loading protein</span>;
  }

  assertIsNonNullishAntibodyAndVersion(antibody);

  const format = antibody.format;
  const proteinIdsByRef: Record<number, string> = {};
  const inaccessibleProteinIdsByRef: Record<number, string> = {};
  const componentLabelByRef: Record<number, string> = {};
  antibody.version.domains.forEach((domain) => {
    const {entity} = domain;
    if (entity == null) {
      return;
    }
    for (const ref of domain.refs) {
      componentLabelByRef[ref] = domain.label;
      proteinIdsByRef[ref] = isInaccessible(entity) ? entity.invalidId : entity.id;
      if (isInaccessible(entity)) {
        inaccessibleProteinIdsByRef[ref] = entity.invalidId;
      }
    }
  });
  antibody.version.chains.forEach((chain) => {
    const {entity} = chain;
    for (const ref of chain.refs) {
      componentLabelByRef[ref] = chain.label;
      proteinIdsByRef[ref] = isInaccessible(entity) ? entity.invalidId : entity.id;
      if (isInaccessible(entity)) {
        inaccessibleProteinIdsByRef[ref] = entity.invalidId;
      }
    }
  });

  const interactivity = {
    hoveredRef,
    selectedRef,
    setHoveredRef,
    setSelectedRef,
    proteinIdsByRef,
    inaccessibleProteinIdsByRef,
  };

  return (
    <div
      className={css(styles.wrapper)}
      onDragOver={(e) => {
        e.preventDefault();
      }}
      ref={wrapperRef}
      {...getTestProps({component: 'AntibodyMap', element: 'root'})}
    >
      {antibody.version.outOfSync && (
        <div className={css(styles.outOfSyncBanner)}>
          <InlineBanner
            bannerType={InlineBannerType.WARNING}
            text={`Antibody is out of sync with its component entities. ${antibody.version.outOfSyncReason}`}
          />
        </div>
      )}
      <AntibodyFeedbackBanner />
      <AntibodyChainLegend
        antibodyFormat={format}
        antibodyVersion={antibody.version}
        interactivity={interactivity}
        antibodyId={antibodyId}
      />
      {(!expandPreview || showMinimap) && (
        <AntibodyDragWrapper antibodyId={antibodyId} domainPreviewRef={domainPreviewRef}>
          <Antibody
            format={format}
            interactivity={interactivity}
            showLabels={showLabels}
            showPopovers={true}
            antibodyId={antibodyId}
          />
        </AntibodyDragWrapper>
      )}
      {selectedRef != null && (
        <div
          className={css(expandPreview ? styles.previewWrapperExpanded : styles.previewWrapper)}
          ref={domainPreviewRef}
        >
          <ComponentPreview
            componentLabel={componentLabelByRef[selectedRef]}
            antibodyId={antibodyId}
            selectedProteinId={proteinIdsByRef[selectedRef]}
            toggleExpandPreview={toggleExpandPreview}
            closePreview={() => {
              setSelectedRef(null);
            }}
          />
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    maxHeight: '100%',
    userSelect: 'none',
  },
  editButton: {
    width: 'fit-content',
    marginLeft: 'auto',
    marginTop: 8,
    marginBottom: 32,
  },
  previewWrapper: {
    flex: '0 0 280px',
    minHeight: 0,
    height: '280px',
  },
  previewWrapperExpanded: {
    flex: '1 0 auto',
    minHeight: 0,
  },
  outOfSyncBanner: {
    marginBottom: 10,
  },
});
